import { FC, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { League } from 'models';
import routes from 'routes';
import { getOrdinalSuffix } from 'utils/getOriginalSuffix';

import { useLazyGetContestsByDateQuery } from 'api/contest';
import { useLazyGetLeaderboardQuery } from 'api/leaderboard';

import Leaderboard from 'components/leaderboard/Leaderboard';
import LeaderboardSkeleton from 'components/leaderboard/LeaderboardSkeleton';
import LeagueFilter from 'components/leaderboard/LeagueFilter';

import { ReactComponent as LeftArrowIcon } from 'icons/left-arr.svg';

dayjs.extend(utc);
dayjs.extend(timezone);

const LeaderboardPage: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsDate = searchParams.get('date') || dayjs().format('YYYY-MM-DD');
  const searchParamsFriends = searchParams.get('friends');
  const searchParamsLeague = searchParams.get('league');
  const searchParamsContestId = searchParams.get('contestId');

  const [getStat, { data: stat, isFetching: isLeaderbordFetching, isError }] = useLazyGetLeaderboardQuery();
  const [
    fetchContestsByDate,
    { data: contests, isLoading: isContestsLoading, isFetching: isContestsFetching, isError: isContestsError },
  ] = useLazyGetContestsByDateQuery();

  useEffect(() => {
    fetchContestsByDate({ date: searchParamsDate });
  }, [searchParamsDate, fetchContestsByDate]);

  useEffect(() => {
    if (contests && contests.length > 0) {
      let targetContestId: number | null = null;

      if (searchParamsLeague && searchParamsLeague !== 'all') {
        const contestForLeague = contests.find((c) => c.league === searchParamsLeague);
        targetContestId = contestForLeague?.id || null;
      } else {
        targetContestId = contests[0]?.id || null;
      }

      if (targetContestId) {
        getStat({
          contestId: targetContestId,
          friends: searchParamsFriends === 'true',
          league: searchParamsLeague as League,
        });
      }
    } else if (contests && contests.length === 0) {
      getStat({
        contestId: null,
        friends: searchParamsFriends === 'true',
        league: searchParamsLeague as League,
      });
    }
  }, [contests, searchParamsLeague, searchParamsFriends, getStat]);

  const handleChangeLeague = (league: League) => {
    setSearchParams((sp) => {
      const newParams = new URLSearchParams(sp);
      newParams.set('league', league);
      return newParams;
    });
  };

  const handleChangeDate = (date: string) => {
    setSearchParams((sp) => {
      const newParams = new URLSearchParams(sp);
      newParams.set('date', date);
      newParams.delete('league');
      return newParams;
    });
  };

  const isFetching = isLeaderbordFetching;
  const isLocked = stat?.currentContest.status === 'locked';
  const isCompleted = stat?.currentContest.status === 'completed';
  const isActive = stat?.currentContest.status === 'active';
  const isLoading = !isFetching && !!stat;

  const dateStr = useMemo(() => {
    if (!stat && contests && contests.length > 0) {
      const firstContest = contests[0];
      const startsAtNewYork = dayjs(firstContest.startsAt).tz('America/Los_Angeles');
      const day = startsAtNewYork.format('D');
      const postfix = getOrdinalSuffix(parseInt(day));
      return startsAtNewYork.format('MMM ') + day + postfix;
    }

    if (stat) {
      const startsAtNewYork = dayjs(stat.currentContest.startsAt).tz('America/Los_Angeles');
      const day = startsAtNewYork.format('D');
      const postfix = getOrdinalSuffix(parseInt(day));
      return startsAtNewYork.format('MMM ') + day + postfix;
    }

    const selectedDate = dayjs(searchParamsDate);
    const day = selectedDate.format('D');
    const postfix = getOrdinalSuffix(parseInt(day));
    return selectedDate.format('MMM ') + day + postfix;
  }, [stat, contests, searchParamsDate]);

  const availableLeagues = useMemo(() => {
    if (!contests) return [];
    return contests.map((c) => c.league);
  }, [contests]);

  const selectedLeague = useMemo(() => {
    if (searchParamsLeague && availableLeagues.includes(searchParamsLeague as League)) {
      return searchParamsLeague as League;
    }
    return availableLeagues[0] || undefined;
  }, [searchParamsLeague, availableLeagues]);

  return (
    <Box display="flex" flexDirection="column" h="100%" gridRow="span 2">
      <Grid gridTemplateColumns="48px 1fr 48px" alignItems="center" gap="10px" w="100%" py="8px" px="12px">
        <Box as={Link} to={routes.home}>
          <LeftArrowIcon />
        </Box>
        <Flex flexDirection="column" alignItems="center" gap="2px">
          <Text textAlign="center" variant="pageTitle" fontSize="14px" lineHeight="1.2" minH="0">
            SBC Daily
          </Text>
          <Text textAlign="center" fontSize="12px" lineHeight="1.1" opacity={0.8} fontWeight="500">
            {dateStr}
          </Text>
        </Flex>
        <Flex />
      </Grid>

      {/* <DateFilter selectedDate={searchParamsDate} onDateChange={handleChangeDate} /> */}

      <LeagueFilter
        selectedLeague={selectedLeague}
        onLeagueChange={handleChangeLeague}
        leagues={availableLeagues}
        isLoading={isContestsLoading || isContestsFetching}
      />

      {isFetching && <LeaderboardSkeleton />}

      {isLoading && (
        <>
          {(isCompleted || isLocked || isActive) && (
            <Leaderboard
              key={stat?.currentContest.id}
              stat={stat}
              participant={stat.me}
              showLeaderboard={!isLocked && !isActive}
              showMySquad={
                (((isLocked && stat.me?.status === 'completed') || (isCompleted && stat.me?.status === 'completed')) &&
                  !isError) ||
                (isActive && stat.me?.status === 'completed')
              }
              showInviteRow={searchParamsFriends === 'true'}
              showNoParticipants={!isLocked}
            />
          )}
        </>
      )}

      {!isFetching && contests && contests.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py="40px"
          px="20px"
          textAlign="center"
        >
          <Text fontSize="16px" color="white" mb="8px">
            No contests available for this date
          </Text>
          <Text fontSize="14px" color="rgba(255, 255, 255, 0.6)">
            Try selecting a different date
          </Text>
        </Box>
      )}

      {isContestsError && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py="40px"
          px="20px"
          textAlign="center"
        >
          <Text fontSize="16px" color="white" mb="8px">
            Error loading contests
          </Text>
          <Text fontSize="14px" color="rgba(255, 255, 255, 0.6)">
            Please try again later
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default LeaderboardPage;
